import { warehouseClient } from "@/infrastructure/http/warehouseClient";

export type ReportFormat = "xlsx" | "pdf";

export interface KardexReportParams {
  format: ReportFormat;
  warehouseId?: string;
  productId?: string;
  categoryId?: string;
  from?: string;
  to?: string;
}

export interface StockReportParams {
  format: ReportFormat;
  warehouseId?: string;
  productId?: string;
  categoryId?: string;
}

export interface BalanceReportParams {
  format: ReportFormat;
  warehouseId?: string;
  productId?: string;
  categoryId?: string;
  from?: string; // YYYY-MM
  to?: string;   // YYYY-MM
}

function buildQuery(params: Record<string, string | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) query.set(key, value);
  }
  return query.toString();
}

function getContentType(format: ReportFormat): string {
  return format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
}

async function downloadReport(url: string, fallbackFilename: string): Promise<void> {
  const response = await warehouseClient.get(url, { responseType: "blob" });

  // Extract filename from Content-Disposition header or use fallback
  const disposition = response.headers["content-disposition"] ?? "";
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  const filename = match?.[1] ?? fallbackFilename;

  // Trigger browser download
  const blob = new Blob([response.data], { type: response.headers["content-type"] ?? getContentType("xlsx") });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export async function exportKardex(params: KardexReportParams): Promise<void> {
  const query = buildQuery({
    format: params.format,
    warehouseId: params.warehouseId,
    productId: params.productId,
    categoryId: params.categoryId,
    from: params.from,
    to: params.to,
  });
  const fallback = `kardex-${params.from ?? "all"}-${params.to ?? "all"}.${params.format}`;
  await downloadReport(`/reports/kardex?${query}`, fallback);
}

export async function exportStock(params: StockReportParams): Promise<void> {
  const query = buildQuery({
    format: params.format,
    warehouseId: params.warehouseId,
    productId: params.productId,
    categoryId: params.categoryId,
  });
  const fallback = `stock-${new Date().toISOString().slice(0, 10)}.${params.format}`;
  await downloadReport(`/reports/stock?${query}`, fallback);
}

export async function exportBalance(params: BalanceReportParams): Promise<void> {
  const query = buildQuery({
    format: params.format,
    warehouseId: params.warehouseId,
    productId: params.productId,
    categoryId: params.categoryId,
    from: params.from,
    to: params.to,
  });
  const fallback = `balance-${params.from ?? "all"}-${params.to ?? "all"}.${params.format}`;
  await downloadReport(`/reports/balance?${query}`, fallback);
}

// --- History ---

export interface ReportHistoryItem {
  id: string;
  reportType: string;
  format: ReportFormat;
  reportName: string;
  dateFrom: string | null;
  dateTo: string | null;
  status: string;
  createdAt: string;
}

export interface ReportHistoryMeta {
  totalItems: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
}

export interface ReportHistoryResponse {
  items: ReportHistoryItem[];
  meta: ReportHistoryMeta;
}

export async function getReportHistory(page = 1, limit = 20): Promise<ReportHistoryResponse> {
  const response = await warehouseClient.get<any>(
    `/reports/history?page=${page}&limit=${limit}`
  );
  // Handle both { items, meta } and { data: { items, meta } } shapes
  const data = response.data?.data ?? response.data;
  return {
    items: data?.items ?? [],
    meta: data?.meta ?? { totalItems: 0, itemsPerPage: limit, totalPages: 0, currentPage: page },
  };
}

export async function getReportDownloadUrl(reportId: string): Promise<string> {
  const response = await warehouseClient.get<{ data: { url: string } }>(
    `/reports/download/${reportId}`
  );
  return response.data.data.url;
}
